import { useCallback, useState } from 'react'
import Confirm from '../components/Confirm'
import ReliquatConfirm, { type ReliquatPopupLine } from '../components/RetailValidation/ReliquatConfirm'
import useTranslation from './useTranslation'
import type { Order } from '../types'

/**
 * useFinalValidation — flux UNIQUE de « Validation finale » (N1), partagé par tous les points
 * d'appel (menus OrderDetail / Orders / CustomerDetail). Encapsule :
 *   1. la détection d'un éventuel reliquat (lignes dont la qté finale < demande client),
 *   2. la popup de confirmation : ReliquatConfirm si reliquat, sinon Confirm simple,
 *   3. l'appel à validateRetailN1 (avec les lignes de reliquat cochées).
 *
 * Les dépendances métier sont injectées (et NON récupérées via useSales en interne) pour éviter
 * de ré-instancier useSales — dont l'effet de dérivation du tarif courant — une seconde fois.
 *
 * Usage :
 *   const { requestFinalValidation, finalValidationPopups } = useFinalValidation({ ... })
 *   // dans le rendu : {finalValidationPopups}
 *   // dans le handler du menu : requestFinalValidation(order, () => { setCurOrder(null); navigate(...) })
 */
export interface FinalValidationDeps {
   validateRetailN1: (order: Order, reliquatLines?: { sku: string, qty: number }[]) => Promise<string | null>
   computeRefusedLines: (origLines: Order['lines'], curLines: Order['lines']) => { sku: string, qty: number }[]
   isRetailOrder: (order: Order | null | undefined) => boolean
   getRetailModelById: (sku: string) => { label?: string } | null | undefined
}

export default function useFinalValidation(deps: FinalValidationDeps) {
   const { validateRetailN1, computeRefusedLines, isRetailOrder, getRetailModelById } = deps
   const { trans } = useTranslation()

   const [order, setOrder] = useState<Order | null>(null)
   const [onDone, setOnDone] = useState<(() => void) | null>(null)
   const [showConfirm, setShowConfirm] = useState(false)
   const [showReliquat, setShowReliquat] = useState(false)
   const [refusedLines, setRefusedLines] = useState<ReliquatPopupLine[]>([])
   const [checked, setChecked] = useState<boolean[]>([])

   const requestFinalValidation = useCallback((ord: Order | null | undefined, done?: () => void) => {
      if (!ord?.id) return
      setOrder(ord)
      setOnDone(() => done ?? null)
      let refused: ReliquatPopupLine[] = []
      if (ord.original_lines && isRetailOrder(ord)) {
         const r = computeRefusedLines(ord.original_lines, ord.lines)
         if (r.length > 0) {
            const curMap = new Map(ord.lines.map(l => [l.sku, l.qty]))
            const origMap = new Map(ord.original_lines.map(l => [l.sku, l.qty]))
            refused = r.map(x => ({
               sku: x.sku,
               label: getRetailModelById(x.sku)?.label ?? '',
               origQty: origMap.get(x.sku) ?? 0,
               curQty: curMap.get(x.sku) ?? 0,
               reliquat: x.qty,
            }))
         }
      }
      setRefusedLines(refused)
      setChecked(refused.map(() => true))
      if (refused.length > 0) setShowReliquat(true)
      else setShowConfirm(true)
   }, [computeRefusedLines, getRetailModelById, isRetailOrder])

   const runValidation = useCallback(async () => {
      if (!order) return
      const reliquatLines = refusedLines.filter((_, i) => checked[i]).map(r => ({ sku: r.sku, qty: r.reliquat }))
      const done = onDone
      setShowConfirm(false)
      setShowReliquat(false)
      setOrder(null)
      setOnDone(null)
      setRefusedLines([])
      setChecked([])
      await validateRetailN1(order, reliquatLines)
      done?.()
   }, [order, refusedLines, checked, onDone, validateRetailN1])

   const cancel = useCallback(() => {
      setShowConfirm(false)
      setShowReliquat(false)
      setOrder(null)
      setOnDone(null)
   }, [])

   const toggle = useCallback((i: number) => setChecked(c => c.map((v, idx) => idx === i ? !v : v)), [])

   const finalValidationPopups = (
      <>
         {showConfirm && (
            <Confirm
               yesno
               title={trans('?Validation finale')}
               text={trans('?msg_validation_finale')}
               onOK={runValidation}
               onCancel={cancel}
            />
         )}
         {showReliquat && (
            <ReliquatConfirm
               title={trans('?Validation finale')}
               lines={refusedLines}
               checked={checked}
               onToggle={toggle}
               onOK={runValidation}
               onCancel={cancel}
            />
         )}
      </>
   )

   return { requestFinalValidation, finalValidationPopups }
}
